import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useBrandProperties } from "../brandPropertiesHook/useBrandProperties";
import ReconfigureConfirmationPopup from "../ReconfigureConfirmationPopup";
import ValidationStatusDialog from "../ValidationStatusDialog";
import { selectUserWebsitePermissions } from "../../../../../features/userSlice";
import { getSiteInfo } from "../../../../../utils/helpers";

export type AutomaticConfigureActionKey =
  | "reconfigure"
  | "edit_properties"
  | "test_again"
  | "continue_setup"
  | "work_in_progress";

export interface AutomaticConfigureAction {
  key: AutomaticConfigureActionKey;
  label: string;
}

// Same decision table as the legacy Brand Configuration Report table's
// per-row action column (BrandConfigurationReportTableData.tsx):
//   Ready                       -> Reconfigure
//   Not Ready + Properties      -> Edit Properties
//   Not Ready + Validations     -> Test Again
//   Not Ready + Fetcher/Unifier/Advisor -> Continue Setup
//   Running / Pending           -> Work in Progress (disabled)
//   Null/Empty integration_status -> Properties (hardcoded) -> Edit Properties
// The Brands module keeps one icon (Automatic Configure) for every
// condition; only the tooltip label/action varies, per spec — this function
// is the single source of truth both BrandsQuickActions.tsx (label/disabled
// state) and useAutomaticConfigure (click behavior) read from.
export function getAutomaticConfigureAction(row: any): AutomaticConfigureAction {
  const integrationStatus = String(row?.integration_status ?? "").trim().toLowerCase();
  const stuckStep = String(row?.stuck_step ?? "").trim().toLowerCase();

  if (integrationStatus === "ready") {
    return { key: "reconfigure", label: "Reconfigure" };
  }
  if (integrationStatus === "running" || integrationStatus === "pending") {
    return { key: "work_in_progress", label: "Work in Progress" };
  }
  if (stuckStep === "validations") {
    return { key: "test_again", label: "Test Again" };
  }
  if (stuckStep === "fetcher" || stuckStep === "unifier" || stuckStep === "advisor") {
    return { key: "continue_setup", label: "Continue Setup" };
  }
  // Not Ready + Properties, a null/empty integration_status (hardcoded to
  // Properties per the table), or any other/unrecognized step.
  return { key: "edit_properties", label: "Edit Properties" };
}

// "Automatic Configure" quick action, shared by every Brands tab table.
// Reuses the existing "Reconfigure" warning popup and Validation Station
// dialog unmodified — only the Reconfigure (Ready) path shows the "PROCEED
// WITH CAUTION" warning first, exactly like the legacy table; every other
// action (Edit Properties / Test Again / Continue Setup) fires immediately,
// matching the legacy table's un-confirmed MuiLink actions.
export function useAutomaticConfigure() {
  const navigate = useNavigate();
  const location = useLocation();
  const { fetchedTestAgainPopupdata, brandIntegrationProcess } = useBrandProperties();
  const websitePermissions = useSelector(selectUserWebsitePermissions) as any;
  const [reconfigureRow, setReconfigureRow] = useState<any>(null);
  const [validationOpen, setValidationOpen] = useState(false);
  const [validationBrandId, setValidationBrandId] = useState<string | number | null>(null);

  const buildEditPropertiesTarget = (row: any) => {
    // Start from the Brands screen's current search params (tab, sort, page,
    // filters, ...) — same pattern as BrandConfigurationReportTableData.tsx's
    // buildBrandPropertiesTarget — so returning to Brands can restore them.
    const params = new URLSearchParams(location.search);
    params.set("brandSharedUid", row.brand_shared_uid ?? "");
    // Not present in the Brands query (5729017198018560) — flagged previously
    // as a field to confirm with backend.
    params.set("bkInterfaceSharedUid", row.backend_interface_shared_uid ?? "");
    params.set("brandId", row.brand_id ?? "");
    return `/operations/brands/edit_properties?${params.toString()}`;
  };

  const openAutomaticConfigure = (row: any) => {
    const action = getAutomaticConfigureAction(row);
    switch (action.key) {
      case "work_in_progress":
        return; // disabled in BrandsQuickActions.tsx — nothing to do
      case "reconfigure":
        setReconfigureRow(row);
        return;
      case "test_again": {
        setValidationBrandId(row.brand_id);
        // Resolve website_shared_uid from the brand's website name via the
        // user's website permissions — same lookup helper (getSiteInfo)
        // TaskNameDAFilter.tsx and others already use for name<->shared_uid
        // mapping. Falls back to the "test" placeholder (same convention
        // used elsewhere in this module) only if no match is found.
        const websiteSharedUid =
          (getSiteInfo(websitePermissions ?? [], row.website_name) as string | null) ??
          "test";
        // Fire-and-forget so the dialog opens immediately (showing its own
        // loading skeleton) instead of waiting on this fetch.
        // Deliberately does NOT refresh the Brands table afterwards:
        // fetchedBrandsData() flips isFetchedBrandsData to false, which makes
        // BrandsTabs.tsx swap the whole tab for BrandsSkeleton. That unmounts
        // the table rendering {automaticConfigureDialogs} along with the
        // validationOpen state living in this hook, so the dialog vanished as
        // soon as the fetch resolved. The dialog reads its data from Redux
        // (isFetchedPopupData), so no table refresh is needed here.
        fetchedTestAgainPopupdata(row.brand_id, websiteSharedUid);
        setValidationOpen(true);
        return;
      }
      case "continue_setup":
        brandIntegrationProcess(row.brand_id);
        return;
      case "edit_properties":
      default:
        navigate(buildEditPropertiesTarget(row));
    }
  };

  const handleReconfigureProceed = () => {
    if (reconfigureRow) {
      navigate(buildEditPropertiesTarget(reconfigureRow));
    }
    setReconfigureRow(null);
  };

  const automaticConfigureDialogs = (
    <>
      <ReconfigureConfirmationPopup
        reconfigureOpen={Boolean(reconfigureRow)}
        handleReconfigureCancel={() => setReconfigureRow(null)}
        handleReconfigureProceed={handleReconfigureProceed}
      />
      <ValidationStatusDialog
        open={validationOpen}
        onCancel={() => setValidationOpen(false)}
        brandId={validationBrandId}
      />
    </>
  );

  return { openAutomaticConfigure, automaticConfigureDialogs };
}
